import { api } from '@/global/services/api'

// defaults
const DEFAULT_STOPS_DURATION_RANGE = [15, 60]
const DEFAULT_LOW_VOLTAGE_VALUE = 10
const DEFAULT_REPORT_BASE_DATA = {}
const DEFAULT_REPORT_DISPLAY_COLUMNS = []
const DEFAULT_REPORT_TYPE = ''
const DEFAULT_REPORT_CATEGORY = ''
const DEFAULT_REPORT_TITLE = ''
const DEFAULT_DATE_FROM = null
const DEFAULT_DATE_TO = null
const DEFAULT_IDLE_VEHICLE_MIN_TIME = 3
const DEFAULT_MAX_SPEED_VALUE = 100
const DEFAULT_SPEED_LIMIT_TOLERANCE = 30
const DEFAULT_FROM_MINUTES = 3
const DEFAULT_TO_MINUTES = 15
const DEFAULT_RADIUS = 50
const DEFAULT_CLEANING_WIDTH = 0.0
const DEFAULT_IGNORE_MAIN_LOCATIONS = false
const DEFAULT_IGNORE_UNLOADED_VEHICLE = false
const DEFAULT_SELECTED_VEHICLE_TYPE = 'satellite-tracking/live-tracking.vehicles'
const DEFAULT_LATITUDE = null
const DEFAULT_LONGITUDE = null
const DEFAULT_SELECTED_SENSOR = {
  id: null,
  type: null,
  name: null,
  cleaning_width_input: null
}
const DEFAULT_AVAILABILITY_STATUS = null
const DEFAULT_SELECTED_PERSONS = []
const DEFAULT_SELECTED_USERS_FOR_EMAIL = []
const DEFAULT_RECURRING_TYPE = null
const DEFAULT_AUTOMATIC_REPORT_CREATION_DATE = null
const DEFAULT_RECEIVE_ON_DATE = false
const DEFAULT_SCHEDULED_SEND_AT = null
const DEFAULT_SELECTED_COLUMNS = []
const DEFAULT_SELECTED_VEHICLES = []
const DEFAULT_SELECTED_LOCATIONS = []
const DEFAULT_TEMPERATURE_RANGES = {}
const DEFAULT_SHOW_REFERENCE_VALUES = false
const DEFAULT_VEHICLE_ASSIGNED_SENSORS = {}
const DEFAULT_VEHICLE_ASSIGNED_TEMP_SENSORS = {}
const DEFAULT_VEHICLE_AVAILABILITY_STATUSES = {}

const state = {
  reportBaseData: DEFAULT_REPORT_BASE_DATA,
  reportDisplayColumns: DEFAULT_REPORT_DISPLAY_COLUMNS,
  reportType: DEFAULT_REPORT_TYPE,
  reportCategory: DEFAULT_REPORT_CATEGORY,
  reportTitle: DEFAULT_REPORT_TITLE,
  dateFrom: DEFAULT_DATE_FROM,
  dateTo: DEFAULT_DATE_TO,
  stopsDurationRange: DEFAULT_STOPS_DURATION_RANGE,
  lowVoltageValue: DEFAULT_LOW_VOLTAGE_VALUE,
  idleVehicleMinTime: DEFAULT_IDLE_VEHICLE_MIN_TIME,
  maxSpeedValue: DEFAULT_MAX_SPEED_VALUE,
  speedLimitTolerance: DEFAULT_SPEED_LIMIT_TOLERANCE,
  fromMinutes: DEFAULT_FROM_MINUTES,
  toMinutes: DEFAULT_TO_MINUTES,
  radius: DEFAULT_RADIUS,
  cleaningWidth: DEFAULT_CLEANING_WIDTH,
  ignoreMainLocations: DEFAULT_IGNORE_MAIN_LOCATIONS,
  ignoreUnloadedVehicle: DEFAULT_IGNORE_UNLOADED_VEHICLE,
  selectedVehicleType: DEFAULT_SELECTED_VEHICLE_TYPE,
  latitude: DEFAULT_LATITUDE,
  longitude: DEFAULT_LONGITUDE,
  selectedSensor: DEFAULT_SELECTED_SENSOR,
  availabilityStatus: DEFAULT_AVAILABILITY_STATUS,
  selectedPersons: DEFAULT_SELECTED_PERSONS,
  selectedUsersForEmail: DEFAULT_SELECTED_USERS_FOR_EMAIL,
  recurringType: DEFAULT_RECURRING_TYPE,
  automaticReportCreationDate: DEFAULT_AUTOMATIC_REPORT_CREATION_DATE,
  receiveOnDate: DEFAULT_RECEIVE_ON_DATE,
  scheduledSendAt: DEFAULT_SCHEDULED_SEND_AT,
  selectedColumns: DEFAULT_SELECTED_COLUMNS,
  selectedVehicles: DEFAULT_SELECTED_VEHICLES,
  selectedLocations: DEFAULT_SELECTED_LOCATIONS,
  temperatureRanges: DEFAULT_TEMPERATURE_RANGES,
  showReferenceValues: DEFAULT_SHOW_REFERENCE_VALUES,
  vehicleAssignedSensorsBetweenDates: DEFAULT_VEHICLE_ASSIGNED_SENSORS,
  vehicleAssignedTempSensBetweenDates: DEFAULT_VEHICLE_ASSIGNED_TEMP_SENSORS,
  vehicleAvailabilityStatuses: DEFAULT_VEHICLE_AVAILABILITY_STATUSES
}

const getters = {
  getReportBaseData: state => state.reportBaseData,
  getReportDisplayColumns: state => state.reportDisplayColumns,
  getReportType: state => state.reportType,
  getReportCategory: state => state.reportCategory,
  getReportTitle: state => state.reportTitle,
  getDateFrom: state => state.dateFrom,
  getDateTo: state => state.dateTo,
  getStopsDurationRange: state => state.stopsDurationRange,
  getLowVoltageValue: state => state.lowVoltageValue,
  getIdleVehicleMinTime: state => state.idleVehicleMinTime,
  getMaxSpeedValue: state => state.maxSpeedValue,
  getSpeedLimitTolerance: state => state.speedLimitTolerance,
  getFromMinutes: state => state.fromMinutes,
  getToMinutes: state => state.toMinutes,
  getRadius: state => state.radius,
  getCleaningWidth: state => state.cleaningWidth,
  getIgnoreMainLocations: state => state.ignoreMainLocations,
  getIgnoreUnloadedVehicle: state => state.ignoreUnloadedVehicle,
  getSelectedVehicleType: state => state.selectedVehicleType,
  getLatitude: state => state.latitude,
  getLongitude: state => state.longitude,
  getSelectedSensor: state => state.selectedSensor,
  getAvailabilityStatus: state => state.availabilityStatus,
  getSelectedPersons: state => state.selectedPersons,
  getSelectedUsersForEmail: state => state.selectedUsersForEmail,
  getRecurringType: state => state.recurringType,
  getAutomaticReportCreationDate: state => state.automaticReportCreationDate,
  getReceiveOnDate: state => state.receiveOnDate,
  getScheduledSendAt: state => state.scheduledSendAt,
  getSelectedColumns: state => state.selectedColumns,
  getSelectedVehicles: state => state.selectedVehicles,
  getSelectedLocations: state => state.selectedLocations,
  getTemperatureRanges: state => state.temperatureRanges,
  getShowReferenceValues: state => state.showReferenceValues,
  getVehicleAssignedSensorsBetweenDates: state => state.vehicleAssignedSensorsBetweenDates,
  getVehicleAssignedTempSensBetweenDates: state => state.vehicleAssignedTempSensBetweenDates,
  getVehicleAvailabilityStatuses: state => state.vehicleAvailabilityStatuses
}

const actions = {
  setReportBaseData ({ commit }, val) {
    commit('SET_REPORT_BASE_DATA', val)
  },

  setReportDisplayColumns ({ commit }, val) {
    commit('SET_REPORT_DISPLAY_COLUMNS', val)
  },

  setReportType ({ commit }, val) {
    commit('SET_REPORT_TYPE', val)
  },

  setReportCategory ({ commit }, val) {
    commit('SET_REPORT_CATEGORY', val)
  },

  setReportTitle ({ commit }, val) {
    commit('SET_REPORT_TITLE', val)
  },

  setDateFrom ({ commit }, val) {
    commit('SET_DATE_FROM', val)
  },

  setDateTo ({ commit }, val) {
    commit('SET_DATE_TO', val)
  },

  setStopsDurationRange ({ commit }, val) {
    commit('SET_STOPS_DURATION_RANGE', val)
  },

  setLowVoltageValue ({ commit }, val) {
    commit('SET_LOW_VOLTAGE_VALUE', val)
  },

  setIdleVehicleMinTime ({ commit }, val) {
    commit('SET_IDLE_VEHICLE_MIN_TIME', val)
  },

  setMaxSpeedValue ({ commit }, val) {
    commit('SET_MAX_SPEED_VALUE', val)
  },

  setSpeedLimitTolerance ({ commit }, val) {
    commit('SET_SPEED_LIMIT_TOLERANCE', val)
  },

  setFromMinutes ({ commit }, val) {
    commit('SET_FROM_MINUTES', val)
  },

  setToMinutes ({ commit }, val) {
    commit('SET_TO_MINUTES', val)
  },

  setRadius ({ commit }, val) {
    commit('SET_RADIUS', val)
  },

  setCleaningWidth ({ commit }, val) {
    commit('SET_CLEANING_WIDTH', val)
  },

  setIgnoreMainLocations ({ commit }, val) {
    commit('SET_IGNORE_MAIN_LOCATIONS', val)
  },

  setIgnoreUnloadedVehicle ({ commit }, val) {
    commit('SET_IGNORE_UNLOADED_VEHICLE', val)
  },

  setSelectedVehicleType ({ commit }, val) {
    commit('SET_SELECTED_VEHICLE_TYPE', val)
  },

  setLatitude ({ commit }, val) {
    commit('SET_LATITUDE', val)
  },

  setLongitude ({ commit }, val) {
    commit('SET_LONGITUDE', val)
  },

  setSelectedSensor ({ commit }, val) {
    commit('SET_SELECTED_SENSOR', val)
  },

  setAvailabilityStatus ({ commit }, val) {
    commit('SET_AVAILABILITY_STATUS', val)
  },

  setSelectedPersons ({ commit }, val) {
    commit('SET_SELECTED_PERSONS', val)
  },

  setSelectedUsersForEmail ({ commit }, val) {
    commit('SET_SELECTED_USERS_FOR_EMAIL', val)
  },

  setRecurringType ({ commit }, val) {
    commit('SET_RECURRING_TYPE', val)
  },

  setAutomaticReportCreationDate ({ commit }, val) {
    commit('SET_AUTOMATIC_REPORT_CREATION_DATE', val)
  },

  setReceiveOnDate ({ commit }, val) {
    commit('SET_RECEIVE_ON_DATE', val)
  },

  setScheduledSendAt ({ commit }, val) {
    commit('SET_SCHEDULED_SEND_AT', val)
  },

  setSelectedColumns ({ commit }, val) {
    commit('SET_SELECTED_COLUMNS', val)
  },

  setSelectedVehicles ({ commit }, val) {
    commit('SET_SELECTED_VEHICLES', val)
  },

  setSelectedLocations ({ commit }, val) {
    commit('SET_SELECTED_LOCATIONS', val)
  },

  setTemperatureRanges ({ commit }, data) {
    commit('SET_TEMPERATURE_RANGES', data)
  },

  setShowReferenceValues ({ commit }, payload) {
    commit('SET_SHOW_REFERENCE_VALUES', payload)
  },

  async fetchVehicleAssignedSensorsBetweenDates ({ commit, state }) {
    if (!state.selectedVehicles.length) {
      return
    }

    try {
      const { data } = await api('satellite-tracking')
        .get('digital-sensor/assigned-sensors-between-dates', {
          vehicle_id: state.selectedVehicles[0].id,
          date_from: state.dateFrom,
          date_to: state.dateTo
        })
      commit('SET_VEHICLE_ASSIGNED_SENSORS_BETWEEN_DATES', data.sensors)
    }
    catch (e) {
      console.dir(e)
    }
  },

  async fetchVehicleAssignedTempSensBetweenDates ({ commit, state }) {
    if (!state.selectedVehicles.length) {
      return
    }

    try {
      const { data } = await api('satellite-tracking')
        .get('temperature-sensor/assigned-sensors-between-dates-new', {
          vehicle_id: state.selectedVehicles[0].id,
          date_from: state.selectedDateFrom,
          date_to: state.selectedDateTo
        })

      commit('SET_VEHICLE_ASSIGNED_TEMP_SENS_BETWEEN_DATES', data)

      Object.keys(data).forEach(sensorKey => {
        commit('SET_TEMPERATURE_RANGES', { key: sensorKey, value: [15, 42] })
      })
    }
    catch (e) {
      console.dir(e)
    }
  },

  async fetchVehicleAvailabilityStatuses ({ commit }) {
    try {
      const { data } = await api('satellite-tracking')
        .get('vehicle-availability-statuses')
      commit('SET_VEHICLE_AVAILABILITY_STATUSES', data)
    }
    catch (e) {
      console.dir(e)
    }
  },

  resetOptions ({ commit }) {
    commit('RESET_DATA')
  }
}

const mutations = {
  SET_REPORT_BASE_DATA: (state, payload) => (state.reportBaseData = payload),

  SET_REPORT_DISPLAY_COLUMNS: (state, payload) => (state.reportDisplayColumns = payload),

  SET_REPORT_TYPE: (state, payload) => (state.reportType = payload),

  SET_REPORT_CATEGORY: (state, payload) => (state.reportCategory = payload),

  SET_REPORT_TITLE: (state, payload) => (state.reportTitle = payload),

  SET_DATE_FROM: (state, payload) => (state.dateFrom = payload),

  SET_DATE_TO: (state, payload) => (state.dateTo = payload),

  SET_STOPS_DURATION_RANGE: (state, payload) => (state.stopsDurationRange = payload),

  SET_LOW_VOLTAGE_VALUE: (state, payload) => (state.lowVoltageValue = payload),

  SET_IDLE_VEHICLE_MIN_TIME: (state, payload) => (state.idleVehicleMinTime = payload),

  SET_MAX_SPEED_VALUE: (state, payload) => (state.maxSpeedValue = payload),

  SET_SPEED_LIMIT_TOLERANCE: (state, payload) => (state.speedLimitTolerance = payload),

  SET_FROM_MINUTES: (state, payload) => (state.fromMinutes = payload),

  SET_TO_MINUTES: (state, payload) => (state.toMinutes = payload),

  SET_RADIUS: (state, payload) => (state.radius = payload),

  SET_CLEANING_WIDTH: (state, payload) => (state.cleaningWidth = payload),

  SET_IGNORE_MAIN_LOCATIONS: (state, payload) => (state.ignoreMainLocations = payload),

  SET_IGNORE_UNLOADED_VEHICLE: (state, payload) => (state.ignoreUnloadedVehicle = payload),

  SET_SELECTED_VEHICLE_TYPE: (state, payload) => (state.selectedVehicleType = payload),

  SET_LATITUDE: (state, payload) => (state.latitude = payload),

  SET_LONGITUDE: (state, payload) => (state.longitude = payload),

  SET_SELECTED_SENSOR: (state, payload) => (state.selectedSensor = payload),

  SET_AVAILABILITY_STATUS: (state, payload) => (state.availabilityStatus = payload),

  SET_SELECTED_PERSONS: (state, payload) => (state.selectedPersons = payload),

  SET_SELECTED_USERS_FOR_EMAIL: (state, payload) => (state.selectedUsersForEmail = payload),

  SET_RECURRING_TYPE: (state, payload) => (state.recurringType = payload),

  SET_AUTOMATIC_REPORT_CREATION_DATE: (state, payload) => (state.automaticReportCreationDate = payload),

  SET_RECEIVE_ON_DATE: (state, payload) => (state.receiveOnDate = payload),

  SET_SCHEDULED_SEND_AT: (state, payload) => (state.scheduledSendAt = payload),

  SET_SELECTED_COLUMNS: (state, payload) => (state.selectedColumns = payload),

  SET_SELECTED_VEHICLES: (state, payload) => (state.selectedVehicles = payload),

  SET_SELECTED_LOCATIONS: (state, payload) => (state.selectedLocations = payload),

  SET_TEMPERATURE_RANGES: (state, payload) => (state.temperatureRanges[payload.key] = payload.value),

  SET_SHOW_REFERENCE_VALUES: (state, payload) => (state.showReferenceValues = payload),

  SET_VEHICLE_ASSIGNED_SENSORS_BETWEEN_DATES: (state, payload) => (
    state.vehicleAssignedSensorsBetweenDates = payload
  ),

  SET_VEHICLE_ASSIGNED_TEMP_SENS_BETWEEN_DATES: (state, payload) => (
    state.vehicleAssignedTempSensBetweenDates = payload
  ),

  SET_VEHICLE_AVAILABILITY_STATUSES: (state, payload) => (
    state.vehicleAvailabilityStatuses = payload
  ),

  RESET_DATA: (state) => {
    state.reportBaseData = DEFAULT_REPORT_BASE_DATA
    state.reportDisplayColumns = DEFAULT_REPORT_DISPLAY_COLUMNS
    state.reportType = DEFAULT_REPORT_TYPE
    state.reportCategory = DEFAULT_REPORT_CATEGORY
    state.reportTitle = DEFAULT_REPORT_TITLE
    state.dateFrom = DEFAULT_DATE_FROM
    state.dateTo = DEFAULT_DATE_TO
    state.stopsDurationRange = DEFAULT_STOPS_DURATION_RANGE
    state.lowVoltageValue = DEFAULT_LOW_VOLTAGE_VALUE
    state.idleVehicleMinTime = DEFAULT_IDLE_VEHICLE_MIN_TIME
    state.maxSpeedValue = DEFAULT_MAX_SPEED_VALUE
    state.speedLimitTolerance = DEFAULT_SPEED_LIMIT_TOLERANCE
    state.fromMinutes = DEFAULT_FROM_MINUTES
    state.toMinutes = DEFAULT_TO_MINUTES
    state.radius = DEFAULT_RADIUS
    state.cleaningWidth = DEFAULT_CLEANING_WIDTH
    state.ignoreMainLocations = DEFAULT_IGNORE_MAIN_LOCATIONS
    state.ignoreUnloadedVehicle = DEFAULT_IGNORE_UNLOADED_VEHICLE
    state.selectedVehicleType = DEFAULT_SELECTED_VEHICLE_TYPE
    state.latitude = DEFAULT_LATITUDE
    state.longitude = DEFAULT_LONGITUDE
    state.selectedSensor = DEFAULT_SELECTED_SENSOR
    state.availabilityStatus = DEFAULT_AVAILABILITY_STATUS
    state.selectedPersons = DEFAULT_SELECTED_PERSONS
    state.selectedUsersForEmail = DEFAULT_SELECTED_USERS_FOR_EMAIL
    state.recurringType = DEFAULT_RECURRING_TYPE
    state.automaticReportCreationDate = DEFAULT_AUTOMATIC_REPORT_CREATION_DATE
    state.receiveOnDate = DEFAULT_RECEIVE_ON_DATE
    state.scheduledSendAt = DEFAULT_SCHEDULED_SEND_AT
    state.selectedColumns = DEFAULT_SELECTED_COLUMNS
    state.selectedVehicles = DEFAULT_SELECTED_VEHICLES
    state.selectedLocations = DEFAULT_SELECTED_LOCATIONS
    state.temperatureRanges = DEFAULT_TEMPERATURE_RANGES
    state.showReferenceValues = DEFAULT_SHOW_REFERENCE_VALUES
    state.vehicleAssignedSensorsBetweenDates = DEFAULT_VEHICLE_ASSIGNED_SENSORS
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
